import { useMutation } from '@apollo/client'
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'

import {
  IBatchInput,
  IEditPrepItemOnHandBatchStandardVariables,
  IPrepItemBatchType,
  editPrepItemOnHandBatchStandard,
} from '../gql'
import {
  ICountTypeData,
  IPrepItemOnHandTableData,
} from '../hooks/usePrepItemOnHandDataSource'

interface IEditPrepItemOnHandModalProps {
  visible: boolean
  onClose: () => void
  record: IPrepItemOnHandTableData | null
  batchTypes: IPrepItemBatchType[]
  onSuccess?: () => void
}

interface IEditableCountType
  extends Omit<ICountTypeData, 'standardUnitsPerBatch'> {
  tempId?: string
  isNew?: boolean
  standardUnitsPerBatch: number | string // Allow string during editing
}

const EditPrepItemOnHandModal = ({
  visible,
  onClose,
  record,
  batchTypes,
  onSuccess,
}: IEditPrepItemOnHandModalProps) => {
  const [countTypes, setCountTypes] = useState<IEditableCountType[]>([])
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  // Filter batch types to only show ONHAND options
  const filteredBatchTypes = batchTypes.filter(
    (bt) => bt.sourceType === 'ONHAND',
  )

  const [editOnHandBatchStandard] = useMutation(editPrepItemOnHandBatchStandard)

  useEffect(() => {
    if (record && visible) {
      // Initialize with existing count types or at least one empty
      const initialCountTypes =
        record.countTypes.length > 0
          ? record.countTypes.map((ct, index) => ({
              ...ct,
              tempId: `existing-${index}`,
              isNew: false,
            }))
          : [
              {
                batchType: '',
                batchTypeId: undefined,
                standardUnitsPerBatch: 0,
                unitId: record.countTypes[0]?.unitId || 0,
                unit: record.countTypes[0]?.unit || 'Ounces',
                tempId: 'new-0',
                isNew: true,
              },
            ]

      setCountTypes(initialCountTypes)
      setError(null)
      setSuccess(false)
    }
  }, [record, visible])

  const handleAddCountType = () => {
    const newCountType: IEditableCountType = {
      batchType: '',
      batchTypeId: undefined,
      standardUnitsPerBatch: 0,
      unitId: countTypes[0]?.unitId || 0,
      unit: countTypes[0]?.unit || 'Ounces',
      tempId: `new-${Date.now()}`,
      isNew: true,
    }

    setCountTypes([...countTypes, newCountType])
  }

  const handleRemoveCountType = (tempId: string) => {
    if (countTypes.length <= 1) {
      setError('At least one count type is required')
      return
    }

    setCountTypes(countTypes.filter((ct) => ct.tempId !== tempId))
    setError(null)
  }

  const handleCountTypeChange = (
    tempId: string,
    field: keyof IEditableCountType,
    value: any,
  ) => {
    setCountTypes(
      countTypes.map((ct) => {
        if (ct.tempId === tempId) {
          const updated = { ...ct, [field]: value }

          // If changing batch type, update the ID
          if (field === 'batchType') {
            const batchType = filteredBatchTypes.find(
              (bt) => bt.batchTypeName === value,
            )
            if (batchType) {
              updated.batchTypeId = batchType.batchTypeId
            }
          }

          return updated
        }
        return ct
      }),
    )
    setError(null)
  }

  const validateCountTypes = () => {
    if (countTypes.length === 0) {
      setError('At least one count type is required')
      return false
    }

    for (const ct of countTypes) {
      if (!ct.batchType) {
        setError('All count types must have a batch type selected')
        return false
      }

      // Parse the value as float and validate
      const value = parseFloat(String(ct.standardUnitsPerBatch))
      if (isNaN(value) || value <= 0) {
        setError(
          'All standard units per batch must be valid numbers greater than 0',
        )
        return false
      }
    }

    // Check for duplicates
    const batchTypes = countTypes.map((ct) => ct.batchType)
    const uniqueBatchTypes = new Set(batchTypes)
    if (batchTypes.length !== uniqueBatchTypes.size) {
      setError('Each count type must be unique')
      return false
    }

    return true
  }

  const handleSave = async () => {
    if (!record) return

    if (!validateCountTypes()) {
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      // Build the batches array for the mutation
      const batches: IBatchInput[] = countTypes
        .filter((ct) => {
          const value = parseFloat(String(ct.standardUnitsPerBatch))
          return ct.batchTypeId && !isNaN(value) && value > 0
        })
        .map((ct, index) => ({
          batch_type_id: ct.batchTypeId!,
          standard_units_per_batch: parseFloat(
            String(ct.standardUnitsPerBatch),
          ),
          display_order: index + 1,
        }))

      // Call the mutation with all batches at once
      await editOnHandBatchStandard({
        variables: {
          iPrepItemId: record.prepItemId,
          iBatches: batches,
        } as IEditPrepItemOnHandBatchStandardVariables,
      })

      setSuccess(true)

      if (onSuccess) {
        onSuccess()
      }

      setTimeout(() => {
        onClose()
        setSuccess(false)
        setError(null)
        setIsLoading(false)
      }, 1500)
    } catch (err) {
      setError('Failed to update. Please try again.')
      setIsLoading(false)
    }
  }

  if (!record) return null

  return (
    <Dialog open={visible} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit Prep Item On Hand - {record.prepItem}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 500 }}>
            Configure count types for On Hand tracking
          </Typography>

          {countTypes.map((countType, index) => (
            <Box
              key={countType.tempId}
              sx={{
                mb: 3,
                p: 2,
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                bgcolor: '#f5f5f5',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Typography variant="subtitle2">
                  Count Type {index + 1}
                </Typography>
                {countTypes.length > 1 && (
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveCountType(countType.tempId!)}
                    disabled={isLoading}
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>

              <Box
                sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}
              >
                <FormControl fullWidth size="small">
                  <InputLabel>Batch Type</InputLabel>
                  <Select
                    value={countType.batchType}
                    onChange={(e) =>
                      handleCountTypeChange(
                        countType.tempId!,
                        'batchType',
                        e.target.value,
                      )
                    }
                    label="Batch Type"
                    disabled={isLoading}
                  >
                    {filteredBatchTypes.map((type) => (
                      <MenuItem
                        key={type.batchTypeId}
                        value={type.batchTypeName}
                      >
                        {type.batchTypeName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  label="Standard Units per Batch"
                  value={countType.standardUnitsPerBatch || ''}
                  onChange={(e) => {
                    const value = e.target.value
                    // Allow any input during typing, validate on save
                    handleCountTypeChange(
                      countType.tempId!,
                      'standardUnitsPerBatch',
                      value,
                    )
                  }}
                  placeholder="Enter a number (e.g., 10 or 10.5)"
                  size="small"
                  fullWidth
                  disabled={isLoading}
                />
              </Box>

              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Unit: {countType.unit}
                </Typography>
              </Box>
            </Box>
          ))}

          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddCountType}
            disabled={isLoading}
            sx={{ mb: 3 }}
            fullWidth
          >
            Add Count Type
          </Button>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Successfully updated
            </Alert>
          )}

          <Box
            sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 4 }}
          >
            <Button onClick={onClose} variant="outlined" disabled={isLoading}>
              Close
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={success || isLoading}
            >
              Save
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default EditPrepItemOnHandModal
